import { useCallback, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { IdSelect } from '../../../../components/IdSelect';
import type { IdOption } from '../../../../components/IdSelect';

import type { FileCategory } from './types/file-category.type';

interface Props {
  readonly categories: readonly FileCategory[];
  readonly count: number;
  readonly error: string | null;
  readonly isLoading: boolean;
  readonly isOpen: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (destinationFileCategoryId: Id) => void;
}

export const MoveFilesDialog = ({
  categories,
  count,
  error,
  isLoading,
  isOpen,
  onCancel,
  onConfirm,
}: Readonly<Props>) => {
  const [destinationFileCategoryId, setDestinationFileCategoryId] = useState<
    Id | typeof EMPTY_STRING
  >(EMPTY_STRING);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setDestinationFileCategoryId(EMPTY_STRING);
    }
  }

  const categoryOptions = useMemo<readonly IdOption<Id>[]>(
    () =>
      categories.map((category) => ({
        id: category._id,
        label: category.label,
      })),
    [categories],
  );

  const handleConfirm = useCallback(() => {
    if (destinationFileCategoryId) {
      onConfirm(destinationFileCategoryId);
    }
  }, [destinationFileCategoryId, onConfirm]);

  return (
    <Dialog
      aria-describedby="move-files-dialog-description"
      aria-labelledby="move-files-dialog-title"
      onClose={onCancel}
      open={isOpen}
    >
      <DialogTitle id="move-files-dialog-title">
        Déplacer {count > 1 ? `${count.toFixed()} fichiers` : 'le fichier'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="move-files-dialog-description">
          Choisissez la catégorie de destination.
        </DialogContentText>
        <IdSelect
          autoFocus
          id="move-files-category"
          label="Catégorie de destination"
          onChange={setDestinationFileCategoryId}
          options={categoryOptions}
          sx={{ mt: 2 }}
          value={destinationFileCategoryId}
        />
        {!!error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size="large">
          Annuler
        </Button>
        <Button
          color="primary"
          disabled={!destinationFileCategoryId}
          loading={isLoading}
          onClick={handleConfirm}
          size="large"
          variant="contained"
        >
          Déplacer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
